import type { FieldTree } from '@angular/forms/signals';

import type { AnimationSettingsLegacy } from '@sites/entity-core';

/**
 * Minimal `data` shape a widget must satisfy to install
 * {@link animationPlugin}. Schema twin in entity-core:
 * `animationPluginShapeLegacy` (`widget/common/schemas/legacy/animation.ts`) —
 * spread it into the widget's data schema.
 */
export type AnimationPluginData = {
    animation: AnimationSettingsLegacy;
};

export type AnimationApi = {
    readonly animation: FieldTree<AnimationSettingsLegacy>;
    setAnimation: (value: AnimationSettingsLegacy) => void;
};

/**
 * Animation settings group as a FieldTree accessor + setter sugar. Receives
 * the form's `data` FieldTree **lazily** (`() => _raw.data`) and exposes
 * getters, so tree paths only materialise on consumer access — compose with
 * {@link import('../../core/compose-form').composeForm}, never object spread.
 */
export function animationPlugin<D extends AnimationPluginData>(data: () => FieldTree<D>): AnimationApi {
    // FieldTree is invariant (writable signals), so the narrowing is done via
    // an explicit cast — safe by the `D extends AnimationPluginData` constraint.
    const d = data as unknown as () => FieldTree<AnimationPluginData>;
    return {
        get animation() {
            return d().animation;
        },
        setAnimation: (value: AnimationSettingsLegacy) => {
            d().animation().value.set(value);
        },
    };
}
